import axios from "axios";
import { celebApi } from "./api";
import { notifyError } from "@/utils/NotificationFunctions";
import { CollectionStatus } from "@/interfaces/personal-collections/personal-collections.interface";
import {
  AddPhotoToPersonalCollectionResponse,
  CreatePersonalCollectionResponse,
  DeletePersonalCollectionResponse,
  DuplicatePersonalCollectionResponse,
  GetPersonalCollectionByIdResponse,
  GetPersonalCollectionsByUserResponse,
  RemovePhotoFromCollectionResponse,
  ReorderPhotoInPersonalCollectionResponse,
  UpdatePersonalCollectionResponse,
} from "@/interfaces/personal-collections/personal-collections.response";

// Base path for this resource
const basePath = `${celebApi}/personal-collections`;

// GET /personal-collections/user/:userId
export async function getPersonalCollectionsByUser(userId: string, userToken: string) {
  const httpOptions = {
    headers: {
      Authorization: userToken,
    },
  };
  try {
    const res = await axios.get<GetPersonalCollectionsByUserResponse>(
      `${basePath}/user/${userId}`,
      httpOptions
    );
    return res.data;
  } catch (error) {
    notifyError("Connection error");
    throw error;
  }
}

// GET /personal-collections/:id
export async function getPersonalCollectionById(id: string) {
  try {
    const res = await axios.get<GetPersonalCollectionByIdResponse>(`${basePath}/${id}`);
    return res.data;
  } catch (error) {
    notifyError("Connection error");
    throw error;
  }
}

// POST /personal-collections
export async function createPersonalCollection(
  data: { name: string; userId: string; status: CollectionStatus },
  token: string
) {
  const httpOptions = {
    headers: { Authorization: token },
  };

  try {
    const res = await axios.post<CreatePersonalCollectionResponse>(`${basePath}`, data, httpOptions);
    return res.data;
  } catch (error) {
    throw error;
  }
}

// PUT /personal-collections/:id
export async function updatePersonalCollection(
  id: string,
  data: { name?: string; status?: CollectionStatus; photos?: string[] },
  token: string
) {
  const httpOptions = {
    headers: { Authorization: token },
  };

  try {
    const res = await axios.put<UpdatePersonalCollectionResponse>(`${basePath}/${id}`, data, httpOptions);
    return res.data;
  } catch (error) {
    throw error;
  }
}

// DELETE /personal-collections/:id
export async function deletePersonalCollection(id: string, token: string) {
  const httpOptions = {
    headers: { Authorization: token },
  };

  try {
    const res = await axios.delete<DeletePersonalCollectionResponse>(`${basePath}/${id}`, httpOptions);
    return res.data;
  } catch (error) {
    throw error;
  }
}

// POST /personal-collections/:id/duplicate
export async function duplicatePersonalCollection(
  id: string,
  data: { name?: string; status?: CollectionStatus; photos?: string[] },
  token: string
) {
  const httpOptions = {
    headers: { Authorization: token },
  };

  try {
    const res = await axios.post<DuplicatePersonalCollectionResponse>(
      `${basePath}/${id}/duplicate`,
      data,
      httpOptions
    );
    return res.data;
  } catch (error) {
    throw error;
  }
}

// POST /personal-collections/:id/photos
export async function addPhotoToPersonalCollection(
  id: string,
  data: { photoId: string; position?: number },
  token: string
) {
  const httpOptions = {
    headers: { Authorization: token },
  };

  try {
    const res = await axios.post<AddPhotoToPersonalCollectionResponse>(
      `${basePath}/${id}/photos`,
      data,
      httpOptions
    );
    return res.data;
  } catch (error) {
    throw error;
  }
}

// PUT /personal-collections/:id/photos/order
export async function reorderPhotoInPersonalCollection(
  id: string,
  data: { photoId: string; position: string },
  token: string
) {
  const httpOptions = {
    headers: { Authorization: token },
  };

  try {
    const res = await axios.put<ReorderPhotoInPersonalCollectionResponse>(
      `${basePath}/${id}/photos/order`,
      data,
      httpOptions
    );
    return res.data;
  } catch (error) {
    throw error;
  }
}

// DELETE /personal-collections/:id/photos/:photoId
export async function removePhotoFromPersonalCollection(id: string, photoId: string, token: string) {
  const httpOptions = {
    headers: { Authorization: token },
  };

  try {
    const res = await axios.delete<RemovePhotoFromCollectionResponse>(
      `${basePath}/${id}/photos/${photoId}`,
      httpOptions
    );
    return res.data;
  } catch (error) {
    throw error;
  }
}
